import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';

interface Review {
  text: string;
  name: string;
  rating: number;
}

const reviews: Review[] = [
  {
    text: 'Beste Werkstatt im Ländle! Qualität, Service, Preisleistung — alles top. Ein großes Lob an Sude für den schnellen Kundenservice.',
    name: 'Kevin Ilic',
    rating: 5,
  },
  {
    text: 'Mein Tesla Model Y wurde schnell und professionell repariert. Innen und außen gereinigt — obwohl nicht erwartet. Rundum toller Service!',
    name: 'Serhat Gaye',
    rating: 5,
  },
  {
    text: 'BMW M4 Competition perfekt repariert. Das gesamte Team war professionell und kompetent — sie haben unsere Anliegen ernst genommen.',
    name: 'Yaren Supermarkt',
    rating: 5,
  },
  {
    text: 'RS6 Fehlermeldungen nach Lenkradumbau — wo andere 1–2 Tage ankündigten, in 15 Minuten erledigt. Absolut beeindruckend!',
    name: 'Aytac Ulutürk',
    rating: 5,
  },
  {
    text: 'Auch Kunden mit kleinerem Budget werden sehr zuvorkommend behandelt. Bei Notfällen dazwischengeschoben. Rundum toller Service!',
    name: 'Bettina',
    rating: 5,
  },
  {
    text: 'Schnelle Terminvergabe, saubere Arbeit, faire Preise. Man merkt, dass hier mit Herzblut gearbeitet wird. Komme definitiv wieder.',
    name: 'Michael K.',
    rating: 5,
  },
];

const slideVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 60 }),
  center: { opacity: 1, x: 0 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -60 }),
};

const ReviewCard: React.FC<{ review: Review }> = ({ review }) => (
  <div className="review-card-content">
<p className="review-text">{review.text}</p>
    <div className="review-footer">
      <span className="review-stars">★★★★★</span>
      <span className="review-name">{review.name}</span>
      <span className="review-source">— Google</span>
    </div>
  </div>
);

const ReviewsSection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Monotonically increasing — never wraps, so React always mounts a fresh motion.div
  const [progressKey, setProgressKey] = useState(0);
  const direction = useRef(1);
  const [isMobile, setIsMobile] = useState(false);
  const [hintDone, setHintDone] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // One-time swipe hint nudge on mobile after 1s
  useEffect(() => {
    if (!hintDone && isMobile) {
      const t = setTimeout(() => {
        setShowHint(true);
        setTimeout(() => { setShowHint(false); setHintDone(true); }, 800);
      }, 1000);
      return () => clearTimeout(t);
    }
  }, [isMobile, hintDone]);

  const advance = (nextIndex: number, dir: number) => {
    direction.current = dir;
    setCurrentIndex(nextIndex);
    setProgressKey(k => k + 1);
  };

  // Auto-advance — resets whenever currentIndex changes
  useEffect(() => {
    const timer = setTimeout(
      () => advance((currentIndex + 1) % reviews.length, 1),
      6000,
    );
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  return (
    <section className="reviews-section">
      <header className="reviews-header">
        <h2>Das sagen unsere Kunden</h2>
        <p className="reviews-rating">
          4.9 <span className="reviews-stars">★★★★★</span> · 172 Bewertungen auf Google
        </p>
      </header>

      <div className="review-stage">
        {/* Outer wrapper handles the one-time mobile swipe hint nudge */}
        <motion.div
          animate={showHint ? { x: [0, -8, 0] } : { x: 0 }}
          transition={showHint ? { duration: 0.6 } : { duration: 0 }}
        >
          <AnimatePresence mode="wait" custom={direction.current}>
            <motion.div
              key={currentIndex}
              custom={direction.current}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: 'easeOut' }}
              drag={isMobile ? 'x' : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
                const swipe = Math.abs(info.offset.x) * info.velocity.x;
                if (swipe < -10000) advance((currentIndex + 1) % reviews.length, 1);
                else if (swipe > 10000) advance((currentIndex - 1 + reviews.length) % reviews.length, -1);
              }}
            >
              <ReviewCard review={reviews[currentIndex]} />
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>

      <div className="progress-track">
        <motion.div
          key={progressKey}
          className="progress-fill"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 6, ease: 'linear' }}
          style={{ transformOrigin: 'left center' }}
        />
      </div>
    </section>
  );
};

export default ReviewsSection;
